import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useToaster } from "../../contexts/ToasterContext.js";
import { getSeverityInfo } from "../../utils/integrationHelpers.js";
import { awsConfig } from "../../config/identity-management/aws.js";
import scansListMock from "../../rawData/iamScanResult.json";
import scanResultMock from "../../rawData/iamScanFinding.json";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card.jsx";

import DynamicBreadcrumbs from "../../components/Breadcrumbs/DynamicBreadcrumbs.jsx";
import { Button } from "../../components/ui/button.jsx";
import { DataTable } from "../../components/ui/data-table.jsx";
import RemediationSidebar from "../../components/RemediationSidebar.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select.jsx";
import {
  Shield,
  RefreshCw,
  Eye,
  AlertCircle,
  Construction,
  ArrowLeft,
  Clock,
  Search,
} from "lucide-react";

const providerConfigs = {
  aws: awsConfig,
};

const SeverityBadge = ({ severity }) => {
  const severityInfo = getSeverityInfo(severity);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityInfo.bg} ${severityInfo.text}`}
    >
      {severityInfo.label}
    </span>
  );
};

// Stats cards shimmer for summary section
const StatsCardsShimmer = () => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 mb-8">
    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
      <Card key={i} className="bg-white border border-[#441a9a] shadow-none">
        <CardHeader className="pb-1.5 px-3 pt-3">
          <div className="animate-pulse">
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-12"></div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

// Scan list shimmer for all scans view
const ScanListShimmer = ({ count = 8 }) => (
  <div className="overflow-hidden border border-gray-300 rounded-lg">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left p-4 text-gray-700 font-medium w-[200px]">
            Scan ID
          </th>
          <th className="text-left p-4 text-gray-700 font-medium w-[150px]">
            Status
          </th>
          <th className="text-left p-4 text-gray-700 font-medium w-[200px]">
            Date
          </th>
          <th className="text-left p-4 text-gray-700 font-medium w-[200px]">
            Findings
          </th>
          <th className="text-left p-4 text-gray-700 font-medium w-[100px]">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: count }).map((_, index) => (
          <tr
            key={index}
            className={index < count - 1 ? "border-b border-gray-100" : ""}
          >
            <td className="p-4">
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
            </td>
            <td className="p-4">
              <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
            </td>
            <td className="p-4">
              <div className="h-4 bg-gray-200 rounded w-36 animate-pulse"></div>
            </td>
            <td className="p-4">
              <div className="h-4 bg-gray-200 rounded w-36 animate-pulse"></div>
            </td>
            <td className="p-4">
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Findings table shimmer
const FindingsTableShimmer = () => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="flex items-center gap-3">
          <div className="animate-pulse">
            <div className="h-9 bg-gray-200 rounded w-[140px]"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-9 bg-gray-200 rounded w-[140px]"></div>
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left p-3 text-gray-700 font-medium">
                Severity
              </th>
              <th className="text-left p-3 text-gray-700 font-medium">
                Finding
              </th>
              <th className="text-left p-3 text-gray-700 font-medium">
                Resource
              </th>
              <th className="text-left p-3 text-gray-700 font-medium">
                Status
              </th>
              <th className="text-left p-3 text-gray-700 font-medium">
                AI Analysis
              </th>
              <th className="text-left p-3 text-gray-700 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, index) => (
              <tr
                key={index}
                className="animate-pulse border-b border-gray-100"
              >
                <td className="p-3">
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </td>
                <td className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                </td>
                <td className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-64 font-mono"></div>
                </td>
                <td className="p-3">
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="flex flex-col gap-1">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="h-8 w-8 bg-gray-200 rounded"></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

// Header shimmer with provider icon and action buttons
const HeaderShimmer = ({ type }) => (
  <div className="flex items-center justify-between animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
      <div>
        <div className="h-7 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-28"></div>
      </div>
    </div>
    <div className="flex gap-2">
      {type === "scan-details" && (
        <>
          <div className="h-9 bg-gray-200 rounded w-32"></div>
          <div className="h-9 bg-gray-200 rounded w-28"></div>
        </>
      )}
      <div className="h-9 bg-gray-200 rounded w-20"></div>
    </div>
  </div>
);

// Full page shimmer component
const FullPageShimmer = ({ type }) => (
  <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
    {/* Breadcrumb + Page Title Shimmer */}
    <div className="mb-8 space-y-2">
      <HeaderShimmer type={type} />
    </div>

    {/* Content Shimmer */}
    {type === "scan-details" ? (
      <>
        <StatsCardsShimmer />
        <FindingsTableShimmer />
      </>
    ) : (
      <ScanListShimmer />
    )}
  </div>
);

const CloudIAMResults = () => {
  const { provider, scanId } = useParams();
  const { showToast } = useToaster();
  const navigate = useNavigate();

  // Use provider from URL params, default to 'aws' if not found
  const currentProvider = provider || "aws";
  const config = providerConfigs[currentProvider] || providerConfigs.aws;

  const [scanResults, setScanResults] = useState(null);
  const [scansListData, setScansListData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter state
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination state for scans list
  const [scansCurrentPage, setScansCurrentPage] = useState(1);
  const [scansPerPage, setScansPerPage] = useState(10);

  // Pagination state for scan results/findings
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(30);

  // Get the appropriate resource field based on provider
  const getResourceField = (finding) => {
    switch (currentProvider) {
      case "aws":
        return finding.resource_arn;
      case "azure":
        return finding.resource_id;
      case "gcp":
        return finding.resource_name;
      default:
        return (
          finding.resource_arn || finding.resource_id || finding.resource_name
        );
    }
  };

  // Map LLM usage entries to findings based on resource_arn and statement_id
  const mapLlmUsageToFindings = (findings, llmUsage) => {
    if (
      !findings ||
      !Array.isArray(findings) ||
      !llmUsage ||
      !Array.isArray(llmUsage)
    ) {
      return findings || [];
    }

    // Create a map for quick lookup: key = `${resource_arn}|${statement_id}`
    const llmUsageMap = new Map();
    llmUsage.forEach((usage) => {
      const key = `${usage.resource_arn || ""}|${usage.statement_id || ""}`;
      if (!llmUsageMap.has(key)) {
        llmUsageMap.set(key, []);
      }
      llmUsageMap.get(key).push(usage);
    });

    // Enhance findings with matching LLM usage
    return findings.map((finding) => {
      const resourceArn = finding.resource_arn || "";
      const statementId = finding.statement_id || "";
      const key = `${resourceArn}|${statementId}`;
      const matchingUsage = llmUsageMap.get(key) || [];

      return {
        ...finding,
        llm_usage: matchingUsage.length > 0 ? matchingUsage : null,
      };
    });
  };

  const fetchScansListData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use local mock JSON for scans list
      const data = scansListMock;
      setScansListData(data);
      setScansCurrentPage(data.pagination?.current_page || 1);
      setScansPerPage(data.pagination?.per_page || 10);
    } catch (err) {
      console.error("Error fetching IAM scans list:", err);
      setError(err.message || "Failed to load IAM scans");
      showToast("Failed to load IAM scans", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchScanResults = async (
    selectedScanId,
    page = 1,
    itemsPerPage = 30
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Validate scanId is a string
      if (!selectedScanId || typeof selectedScanId !== "string") {
        throw new Error("Invalid scan ID");
      }

      // Use local mock JSON for scan results (same data reused for any scanId)
      const data = scanResultMock;
      const scanData = data.response || data;
      setScanResults({
        ...scanData,
        pagination: data.pagination,
      });
      setResultsCurrentPage(data.pagination?.current_page || page);
      setResultsPerPage(data.pagination?.per_page || itemsPerPage);
    } catch (err) {
      console.error("Error fetching IAM scan results:", err);
      setError(err.message || "Failed to load scan results");
      showToast("Failed to load scan results", "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial load using local mock data
  useEffect(() => {
    if (scanId) {
      // Load mock results for a specific scan
      fetchScanResults(scanId);
    } else {
      // Load mock list of scans
      fetchScansListData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId]);
  // Parse severity counts from analysis_summary
  const parseSeverityCounts = () => {
    const analysisSummary = scanResults?.scan_results?.analysis_summary;

    if (!analysisSummary) {
      // Fallback: calculate from findings if analysis_summary is not available
      if (!scanResults?.scan_results?.findings) return {};

      return scanResults.scan_results.findings.reduce((acc, finding) => {
        const severity = finding.severity || "INFORMATIONAL";
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      }, {});
    }

    // Parse the analysis_summary text
    const counts = {
      total: 0,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      informational: 0,
    };

    // Extract total findings
    const totalMatch = analysisSummary.match(/Total findings:\s*(\d+)/i);
    if (totalMatch) {
      counts.total = parseInt(totalMatch[1], 10);
    }

    // Extract severity breakdown
    const criticalMatch = analysisSummary.match(/Critical:\s*(\d+)/i);
    if (criticalMatch) {
      counts.critical = parseInt(criticalMatch[1], 10);
    }

    const highMatch = analysisSummary.match(/High:\s*(\d+)/i);
    if (highMatch) {
      counts.high = parseInt(highMatch[1], 10);
    }

    const mediumMatch = analysisSummary.match(/Medium:\s*(\d+)/i);
    if (mediumMatch) {
      counts.medium = parseInt(mediumMatch[1], 10);
    }

    const lowMatch = analysisSummary.match(/Low:\s*(\d+)/i);
    if (lowMatch) {
      counts.low = parseInt(lowMatch[1], 10);
    }

    const informationalMatch = analysisSummary.match(/Informational:\s*(\d+)/i);
    if (informationalMatch) {
      counts.informational = parseInt(informationalMatch[1], 10);
    }

    return counts;
  };

  const severityCounts = parseSeverityCounts();



  // Check if this is a "coming soon" provider (no findings implementation yet)
  const isComingSoon =
    currentProvider !== "aws" &&
    scanId &&
    (!scanResults?.scan_results?.findings ||
      scanResults.scan_results.findings.length === 0);

  // Sidebar handlers
  const handleRowClick = (finding) => {
    setSelectedFinding(finding);
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setSelectedFinding(null);
  };

  // Get enhanced findings with LLM usage mapped, filtered, and sorted by severity
  const getEnhancedFindings = () => {
    if (!scanResults?.scan_results?.findings) return [];
    const findings = scanResults.scan_results.findings;
    const llmUsage = scanResults.scan_results.llm_usage || [];
    let enhancedFindings = mapLlmUsageToFindings(findings, llmUsage);

    // Apply severity filter
    if (severityFilter !== "all") {
      enhancedFindings = enhancedFindings.filter((finding) => {
        const severity = (finding.severity || "").toUpperCase();
        return severity === severityFilter.toUpperCase();
      });
    }

    // Apply status filter
    if (statusFilter !== "all") {
      enhancedFindings = enhancedFindings.filter((finding) => {
        const status = (finding.status || "open").toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }

    // Sort by severity: Critical → High → Medium → Low → Unknown
    const severityOrder = {
      CRITICAL: 1,
      critical: 1,
      HIGH: 2,
      high: 2,
      MEDIUM: 3,
      medium: 3,
      LOW: 4,
      low: 4,
      INFORMATIONAL: 5,
      informational: 5,
      UNKNOWN: 6,
      unknown: 6,
    };

    return enhancedFindings.sort((a, b) => {
      const severityA = a.severity || "UNKNOWN";
      const severityB = b.severity || "UNKNOWN";
      const orderA = severityOrder[severityA] || 7;
      const orderB = severityOrder[severityB] || 7;
      return orderA - orderB;
    });
  };

  // Get filter counts
  const getFilterCounts = () => {
    if (!scanResults?.scan_results?.findings) {
      return { severity: {}, status: {} };
    }

    const findings = scanResults.scan_results.findings;
    const severityCounts = {};
    const statusCounts = {};

    findings.forEach((finding) => {
      const severity = (finding.severity || "UNKNOWN").toUpperCase();
      const status = (finding.status || "open").toLowerCase();

      severityCounts[severity] = (severityCounts[severity] || 0) + 1;
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return { severity: severityCounts, status: statusCounts };
  };

  const filterCounts = getFilterCounts();

  // Format number with commas
  const formatNumber = (num) => {
    if (num == null) return "0";
    return num.toLocaleString();
  };

  // Pagination handlers for scans list
  const handleScansPrevPage = () => {
    if (scansCurrentPage > 1) {
      fetchScansListData(scansCurrentPage - 1, scansPerPage);
    }
  };

  const handleScansNextPage = () => {
    if (
      scansListData?.pagination &&
      scansCurrentPage < scansListData.pagination.total_pages
    ) {
      fetchScansListData(scansCurrentPage + 1, scansPerPage);
    }
  };

  const handleScansPerPageChange = (value) => {
    const newPerPage = parseInt(value, 10);
    setScansPerPage(newPerPage);
    fetchScansListData(1, newPerPage);
  };

  // Pagination handlers for scan results
  const handleResultsPrevPage = () => {
    if (resultsCurrentPage > 1) {
      fetchScanResults(scanId, resultsCurrentPage - 1, resultsPerPage);
    }
  };

  const handleResultsNextPage = () => {
    if (
      scanResults?.pagination &&
      resultsCurrentPage < scanResults.pagination.total_pages
    ) {
      fetchScanResults(scanId, resultsCurrentPage + 1, resultsPerPage);
    }
  };

  const handleResultsPerPageChange = (value) => {
    const newPerPage = parseInt(value, 10);
    setResultsPerPage(newPerPage);
    fetchScanResults(scanId, 1, newPerPage);
  };

  // Generate pagination buttons for scans list
  const generateScansPaginationButtons = () => {
    if (!scansListData?.pagination) return [];

    const { current_page, total_pages } = scansListData.pagination;
    const buttons = [];
    const maxButtons = 5;

    if (total_pages <= maxButtons) {
      for (let i = 1; i <= total_pages; i++) {
        buttons.push(i);
      }
    } else {
      if (current_page <= 3) {
        buttons.push(1, 2, 3, 4, "...", total_pages);
      } else if (current_page >= total_pages - 2) {
        buttons.push(
          1,
          "...",
          total_pages - 3,
          total_pages - 2,
          total_pages - 1,
          total_pages
        );
      } else {
        buttons.push(
          1,
          "...",
          current_page - 1,
          current_page,
          current_page + 1,
          "...",
          total_pages
        );
      }
    }

    return buttons;
  };

  // Generate pagination buttons for scan results
  const generateResultsPaginationButtons = () => {
    if (!scanResults?.pagination) return [];

    const { current_page, total_pages } = scanResults.pagination;
    const buttons = [];
    const maxButtons = 5;

    if (total_pages <= maxButtons) {
      for (let i = 1; i <= total_pages; i++) {
        buttons.push(i);
      }
    } else {
      if (current_page <= 3) {
        buttons.push(1, 2, 3, 4, "...", total_pages);
      } else if (current_page >= total_pages - 2) {
        buttons.push(
          1,
          "...",
          total_pages - 3,
          total_pages - 2,
          total_pages - 1,
          total_pages
        );
      } else {
        buttons.push(
          1,
          "...",
          current_page - 1,
          current_page,
          current_page + 1,
          "...",
          total_pages
        );
      }
    }

    return buttons;
  };

  // Table columns configuration
  const getTableColumns = () => [
    {
      accessorKey: "severity",
      header: "Severity",
      cell: (value) => <SeverityBadge severity={value} />,
    },
    {
      accessorKey: "title",
      header: "Finding",
      cell: (value) => (
        <div
          className="font-medium text-gray-900 max-w-xs truncate"
          title={value}
        >
          {value}
        </div>
      ),
    },
    {
      accessorKey: "resource",
      header: "Resource",
      cell: (_, row) => {
        const resource = getResourceField(row);
        const parts = resource.split(":");
        const displayText =
          parts.length > 5 ? `...${parts[parts.length - 1]}` : resource;
        return (
          <div
            className="font-mono text-sm text-gray-600 max-w-xs truncate"
            title={resource}
          >
            {displayText}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (_, row) => {
        const status = row.status || "open";
        const statusConfig = {
          open: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Open" },
          in_progress: {
            bg: "bg-blue-100",
            text: "text-blue-800",
            label: "In Progress",
          },
          resolved: {
            bg: "bg-green-100",
            text: "text-green-800",
            label: "Resolved",
          },
          ignored: {
            bg: "bg-gray-100",
            text: "text-gray-800",
            label: "Ignored",
          },
        };
        const config = statusConfig[status] || statusConfig.open;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            {config.label}
          </span>
        );
      },
    },
    {
      accessorKey: "compliances",
      header: "Compliances",
      cell: (_, row) => {
        const compliances = row.compliances || [];
        if (!compliances || compliances.length === 0) {
          return <span className="text-xs text-gray-400">N/A</span>;
        }
        return (
          <div className="group relative flex flex-wrap gap-1">
            {compliances.slice(0, 2).map((compliance, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                title={compliance}
              >
                {compliance.length > 20
                  ? compliance.substring(0, 17) + "..."
                  : compliance}
              </span>
            ))}
            {compliances.length > 2 && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 group-hover:hidden">
                +{compliances.length - 2}
              </span>
            )}

            {/* Hover Tooltip - Show all compliances */}
            {compliances.length > 2 && (
              <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-64 max-w-80">
                <div className="space-y-2">
                  {compliances.map((compliance, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2"
                      title={compliance}
                    >
                      {compliance}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "tags",
      header: "Tags",
      cell: (_, row) => {
        const tags = row.tags || [];
        if (!tags || tags.length === 0) {
          return <span className="text-xs text-gray-400">N/A</span>;
        }
        return (
          <div className="group relative flex flex-wrap gap-1">
            {tags.slice(0, 2).map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800"
                title={tag}
              >
                {tag.length > 20 ? tag.substring(0, 17) + "..." : tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 group-hover:hidden">
                +{tags.length - 2}
              </span>
            )}

            {/* Hover Tooltip - Show all tags */}
            {tags.length > 2 && (
              <div className="hidden group-hover:block absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-64 max-w-80">
                <div className="space-y-2">
                  {tags.map((tag, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 mr-2 mb-2"
                      title={tag}
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: (_, row) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          className="h-8 px-2"
        >
          <Eye className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  if (loading) {
    // Show full page shimmer with no UI shift
    return <FullPageShimmer type={scanId ? "scan-details" : "scan-list"} />;
  }

  if (error) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <Card>
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-gray-900 font-semibold mb-4">{error}</p>
            <Button
              onClick={() =>
                scanId ? fetchScanResults(scanId) : fetchScansListData()
              }
              className="bg-[#44087D] text-white hover:bg-[#5a0a9a]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!scanResults && !scansListData) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-600">No scan results found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate filtered scans list for display
  const filteredScansList =
    scansListData?.data?.filter((scan) =>
      scan.scan_id.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      {/* Breadcrumb + Page Title */}
      <div className="mb-8 space-y-2">
        <DynamicBreadcrumbs />
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {scanId && !isComingSoon && (
              <Button
                onClick={() => {
                  navigate(`/identity-management/iam/${currentProvider}`);
                }}
                variant="outline"
                className="border-none text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 " />
              </Button>
            )}
            <img
              src={config.icon}
              alt={config.provider.name}
              className="w-16 h-16 object-contain"
            />
            <div>
              {/* <h1 className="text-black text-2xl md:text-3xl font-bold">
                {config.displayName}
              </h1> */}
              {scanId && <p className="text-gray-600">Scan ID: {scanId}</p>}
              {!scanId && (
                <p className="text-gray-600">Security Analysis Results</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isComingSoon && (
              <>
                {/* {scanId && (
                  <Button 
                    onClick={handleExport}
                    variant="outline" 
                    className="border-[#44087D] text-[#44087D] hover:bg-[#44087D] hover:text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Report
                  </Button>
                )} */}
                <Button
                  onClick={() => navigate("/manage-scans")}
                  className="bg-[#44087D] text-white hover:bg-[#5a0a9a]"
                >
                  Schedule New Scan
                </Button>
                <Button
                  onClick={() =>
                    scanId ? fetchScanResults(scanId) : fetchScansListData()
                  }
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </>
            )}
            {isComingSoon && (
              <Button asChild variant="outline">
                <Link to="/identity-management">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Identity Management
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {!scanId && scansListData ? (
        <>
          {/* Dashboard Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    Total Scans
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {scansListData.pagination?.total_count ||
                      scansListData.data?.length ||
                      0}
                  </h3>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <RefreshCw className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    Critical Scans
                  </p>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {scansListData.data?.filter(
                      (s) => s.status === "in_progress"
                    ).length || 0}
                  </h3>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Last Scan</p>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {scansListData.data?.[0]?.created_at
                      ? new Date(
                          scansListData.data[0].created_at
                        ).toLocaleDateString()
                      : "N/A"}
                  </h3>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search scans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
              />
            </div>
          </div>

          {/* Scans List View */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Recent IAM Scans
            </h2>
            {filteredScansList.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No IAM Scans Found
                </h3>
                <p className="text-gray-600 text-sm">
                  {scansListData.data?.length === 0
                    ? "No IAM scans have been completed yet. Scans will appear here once they are scheduled and completed."
                    : "No scans match your search criteria."}
                </p>
              </div>
            ) : (
              <div className="overflow-hidden border border-gray-300 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-4 text-gray-700 font-medium">
                        Status
                      </th>
                      <th className="text-left p-4 text-gray-700 font-medium">
                        Scan ID
                      </th>
                      <th className="text-left p-4 text-gray-700 font-medium">
                        Date
                      </th>
                      <th className="text-left p-4 text-gray-700 font-medium">
                        Findings
                      </th>
                      <th className="text-right p-4 text-gray-700 font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScansList.map((scan, index) => {
                      const statusColor =
                        scan.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : scan.status === "in_progress"
                          ? "bg-blue-100 text-blue-800"
                          : scan.status === "failed"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800";

                      return (
                        <tr
                          key={scan.scan_id}
                          onClick={() =>
                            navigate(
                              `/identity-management/iam/${currentProvider}/${scan.scan_id}`
                            )
                          }
                          className={`cursor-pointer ${
                            index < filteredScansList.length - 1
                              ? "border-b border-gray-100 hover:bg-gray-50"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="p-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}
                            >
                              {scan.status || "unknown"}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="font-mono text-gray-900 text-xs">
                              {scan.scan_id.substring(0, 12)}...
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-gray-900 font-medium">
                              {new Date(scan.created_at).toLocaleString()}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="flex items-center gap-1"
                                title="Critical"
                              >
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                <span className="text-sm text-gray-700">
                                  {scan.critical_findings || 0}
                                </span>
                              </div>
                              <div
                                className="flex items-center gap-1"
                                title="High"
                              >
                                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                <span className="text-sm text-gray-700">
                                  {scan.high_findings || 0}
                                </span>
                              </div>
                              <div
                                className="flex items-center gap-1"
                                title="Medium"
                              >
                                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                <span className="text-sm text-gray-700">
                                  {scan.medium_findings || 0}
                                </span>
                              </div>
                              <div
                                className="flex items-center gap-1"
                                title="Low"
                              >
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <span className="text-sm text-gray-700">
                                  {scan.low_findings || 0}
                                </span>
                              </div>
                              <div
                                className="flex items-center gap-1"
                                title="Informational"
                              >
                                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                                <span className="text-sm text-gray-700">
                                  {scan.informational_findings || 0}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <Button
                              variant="ghost"
                              className="text-[#44087D] hover:text-[#5a0a9a] h-8"
                            >
                              View Results
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls for Scans List */}
            {scansListData?.pagination &&
              scansListData.pagination.total_pages > 1 && (
                <div className="mt-4 pt-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-600">
                      Showing{" "}
                      {(scansListData.pagination.current_page - 1) *
                        scansListData.pagination.per_page +
                        1}{" "}
                      to{" "}
                      {Math.min(
                        scansListData.pagination.current_page *
                          scansListData.pagination.per_page,
                        scansListData.pagination.total
                      )}{" "}
                      of {scansListData.pagination.total} scans
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleScansPrevPage}
                        disabled={scansCurrentPage <= 1}
                        variant="outline"
                        size="sm"
                      >
                        Previous
                      </Button>

                      {generateScansPaginationButtons().map((pageNum, index) =>
                        pageNum === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-gray-400"
                          >
                            ...
                          </span>
                        ) : (
                          <Button
                            key={pageNum}
                            onClick={() =>
                              fetchScansListData(pageNum, scansPerPage)
                            }
                            variant={
                              pageNum === scansCurrentPage
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className={
                              pageNum === scansCurrentPage
                                ? "bg-[#44087D] hover:bg-[#44087D]/90 text-white"
                                : ""
                            }
                          >
                            {pageNum}
                          </Button>
                        )
                      )}

                      <Button
                        onClick={handleScansNextPage}
                        disabled={
                          scansCurrentPage >=
                          scansListData.pagination.total_pages
                        }
                        variant="outline"
                        size="sm"
                      >
                        Next
                      </Button>

                      <Select
                        value={scansPerPage.toString()}
                        onValueChange={handleScansPerPageChange}
                      >
                        <SelectTrigger className="w-[75px] h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5 </SelectItem>
                          <SelectItem value="10">10 </SelectItem>
                          <SelectItem value="20">20 </SelectItem>
                          <SelectItem value="50">50 </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </>
      ) : isComingSoon ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Construction className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {config.provider.name} IAM Results Coming Soon
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {config.description} results will be available here once the
              backend implementation is complete. The scan scheduling is already
              functional in the Manage Scans section.
            </p>
            <div className="flex justify-center gap-4">
              <Button asChild>
                <Link to="/manage-scans">
                  <Shield className="w-4 h-4 mr-2" />
                  Configure {config.provider.name} IAM Scans
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/identity-management/iam/aws">
                  View AWS IAM Results
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 mb-8">
            {/* Total Findings */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  Total Findings
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(
                    severityCounts.total ||
                      scanResults?.scan_results?.findings?.length ||
                      0
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Critical Issues */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  Critical Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(severityCounts.critical || 0)}
                </div>
              </CardContent>
            </Card>

            {/* High Issues */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  High Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(severityCounts.high || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Medium Issues */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  Medium Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(severityCounts.medium || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Low Issues */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  Low Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(severityCounts.low || 0)}
                </div>
              </CardContent>
            </Card>

            {/* Informational Issues */}
            <Card className="bg-white border border-[#441a9a] shadow-none">
              <CardHeader className="pb-1.5 px-3 pt-3">
                <CardTitle className="text-xs font-medium text-gray-600">
                  Informational
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(severityCounts.informational || 0)}
                </div>
              </CardContent>
            </Card>

            {/* AI Analysis */}
            {scanResults?.scan_results?.total_llm_usage && (
              <Card className="bg-white border border-[#441a9a] shadow-none">
                <CardHeader className="pb-1.5 px-3 pt-3">
                  <CardTitle className="text-xs font-medium text-gray-600">
                    AI Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatNumber(
                      scanResults.scan_results.total_llm_usage.total_tokens || 0
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {formatNumber(
                      scanResults.scan_results.total_llm_usage.total_requests ||
                        0
                    )}{" "}
                    requests
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Findings Section */}
          {scanResults?.scan_results?.findings &&
            scanResults.scan_results.findings.length > 0 && (
              <div className="mb-8">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        Security Findings
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        {/* Severity Filter */}
                        <Select
                          value={severityFilter}
                          onValueChange={setSeverityFilter}
                        >
                          <SelectTrigger className="w-[140px] h-9">
                            <SelectValue placeholder="Severity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              All Severities (
                              {scanResults.scan_results.findings.length})
                            </SelectItem>
                            <SelectItem value="critical">
                              Critical (
                              {filterCounts.severity.CRITICAL ||
                                filterCounts.severity.critical ||
                                0}
                              )
                            </SelectItem>
                            <SelectItem value="high">
                              High (
                              {filterCounts.severity.HIGH ||
                                filterCounts.severity.high ||
                                0}
                              )
                            </SelectItem>
                            <SelectItem value="medium">
                              Medium (
                              {filterCounts.severity.MEDIUM ||
                                filterCounts.severity.medium ||
                                0}
                              )
                            </SelectItem>
                            <SelectItem value="low">
                              Low (
                              {filterCounts.severity.LOW ||
                                filterCounts.severity.low ||
                                0}
                              )
                            </SelectItem>
                            <SelectItem value="informational">
                              Informational (
                              {filterCounts.severity.INFORMATIONAL ||
                                filterCounts.severity.informational ||
                                0}
                              )
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        {/* Status Filter */}
                        <Select
                          value={statusFilter}
                          onValueChange={setStatusFilter}
                        >
                          <SelectTrigger className="w-[140px] h-9">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              All Status (
                              {scanResults.scan_results.findings.length})
                            </SelectItem>
                            <SelectItem value="open">
                              Open ({filterCounts.status.open || 0})
                            </SelectItem>
                            <SelectItem value="in_progress">
                              In Progress (
                              {filterCounts.status.in_progress || 0})
                            </SelectItem>
                            <SelectItem value="resolved">
                              Resolved ({filterCounts.status.resolved || 0})
                            </SelectItem>
                            <SelectItem value="ignored">
                              Ignored ({filterCounts.status.ignored || 0})
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <DataTable
                      columns={getTableColumns()}
                      data={getEnhancedFindings()}
                      onRowClick={handleRowClick}
                      emptyMessage="No security findings found"
                    />

                    {/* Pagination Controls for Scan Results */}
                    {scanResults?.pagination &&
                      scanResults.pagination.total_pages > 1 && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-gray-600">
                              Showing{" "}
                              {(scanResults.pagination.current_page - 1) *
                                scanResults.pagination.per_page +
                                1}{" "}
                              to{" "}
                              {Math.min(
                                scanResults.pagination.current_page *
                                  scanResults.pagination.per_page,
                                scanResults.pagination.total
                              )}{" "}
                              of {scanResults.pagination.total} findings
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                onClick={handleResultsPrevPage}
                                disabled={resultsCurrentPage <= 1}
                                variant="outline"
                                size="sm"
                              >
                                Previous
                              </Button>

                              {generateResultsPaginationButtons().map(
                                (pageNum, index) =>
                                  pageNum === "..." ? (
                                    <span
                                      key={`ellipsis-${index}`}
                                      className="px-2 text-gray-400"
                                    >
                                      ...
                                    </span>
                                  ) : (
                                    <Button
                                      key={pageNum}
                                      onClick={() =>
                                        fetchScanResults(
                                          scanId,
                                          pageNum,
                                          resultsPerPage
                                        )
                                      }
                                      variant={
                                        pageNum === resultsCurrentPage
                                          ? "default"
                                          : "outline"
                                      }
                                      size="sm"
                                      className={
                                        pageNum === resultsCurrentPage
                                          ? "bg-[#44087D] hover:bg-[#44087D]/90"
                                          : ""
                                      }
                                    >
                                      {pageNum}
                                    </Button>
                                  )
                              )}

                              <Button
                                onClick={handleResultsNextPage}
                                disabled={
                                  resultsCurrentPage >=
                                  scanResults.pagination.total_pages
                                }
                                variant="outline"
                                size="sm"
                              >
                                Next
                              </Button>

                              <Select
                                value={resultsPerPage.toString()}
                                onValueChange={handleResultsPerPageChange}
                              >
                                <SelectTrigger className="w-[100px] h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="10">10</SelectItem>
                                  <SelectItem value="20">20</SelectItem>
                                  <SelectItem value="30">30</SelectItem>
                                  <SelectItem value="50">50</SelectItem>
                                  <SelectItem value="100">100</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              </div>
            )}

          {/* Recommendations Section */}
          {/* {scanResults?.scan_results?.recommendations && scanResults.scan_results.recommendations.length > 0 && (
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Security Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {scanResults.scan_results.recommendations.map((recommendation, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200"
                      >
                        <p className="text-gray-700">{recommendation}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )} */}
        </>
      )}

      {/* Remediation Sidebar */}
      <RemediationSidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        finding={selectedFinding}
      />
    </div>
  );
};

export default CloudIAMResults;
